// Moved out of pages/CanvasManagerAudit.tsx on Oct 19, 2026 so the Today
// tab's area list can open the same map. Only change besides the move: the
// pins plotted are the ones inside the polygon now (see fetchPinsInside).

import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../../lib/supabase";

// ─────────────────────────────────────────────────────────────────────────
// AreaMapStormDrawer — inline, expandable map + storm panel for one assigned
// area (May 22, 2026). Answers two field questions managers kept hitting:
//   1. "Where IS this area?" — a small satellite map with the lasso boundary
//      outlined in Bardin orange + the area's knock pins, fit to the boundary.
//   2. "What hail has it seen?" — every NEXRAD radar hail signature whose
//      center falls within the area (centroid + bounding radius), last 12
//      months, sorted biggest-first with date + POH/POSH confidence.
// Reuses the same Mapbox CDN global + RPCs the /canvas map and Property tab
// use, so the outline and hail numbers match what reps already see.
// ─────────────────────────────────────────────────────────────────────────

declare const mapboxgl: any;
const AREA_MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;
const AREA_STORM_DAYS = 365; // full insurance claim window

interface AreaStormSig {
  id: string;
  occurred_at: string;
  lat: number;
  lon: number;
  max_size_in: number;
  sev_prob: number | null; // POSH
  prob: number | null;     // POH
}

// Extract all rings ([[lon,lat],...]) from a GeoJSON Polygon/MultiPolygon.
function ringsFromGeometry(geom: any): number[][][] {
  if (!geom) return [];
  if (geom.type === "Polygon") return (geom.coordinates ?? []) as number[][][];
  if (geom.type === "MultiPolygon")
    return ((geom.coordinates ?? []) as number[][][][]).flatMap((poly) => poly);
  return [];
}

function ringsBBox(rings: number[][][]) {
  let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
  for (const ring of rings) {
    for (const pt of ring) {
      const lon = pt[0], lat = pt[1];
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
  }
  return { minLon, minLat, maxLon, maxLat };
}

function haversineMetersAM(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

// Severity → tailwind classes for the hail-size badge.
function hailBadgeClasses(sizeIn: number): string {
  if (sizeIn >= 2) return "bg-red-100 text-red-800";
  if (sizeIn >= 1.5) return "bg-orange-100 text-orange-800";
  if (sizeIn >= 1) return "bg-amber-100 text-amber-800";
  return "bg-slate-100 text-slate-600";
}

// Pins INSIDE the polygon right now (Oct 19, 2026) — the same definition
// canvas_area_stats() counts with, so the dots match the "N pins" figure. The
// old drawer plotted the pin_ids snapshot taken when the lasso was drawn,
// which misses pins dropped since and still lists deleted ones. Bbox query
// (paged past the 1,000-row PostgREST cap) + even-odd ray casting.
function pointInRings(lon: number, lat: number, rings: number[][][]): boolean {
  let inside = false;
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i][0], yi = ring[i][1];
      const xj = ring[j][0], yj = ring[j][1];
      if (((yi > lat) !== (yj > lat)) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
    }
  }
  return inside;
}

async function fetchPinsInside(
  rings: number[][][],
  bb: { minLon: number; minLat: number; maxLon: number; maxLat: number },
): Promise<Array<{ id: string; lat: number; lon: number; current_state: string }>> {
  const out: Array<{ id: string; lat: number; lon: number; current_state: string }> = [];
  for (let from = 0; from < 20000; from += 1000) {
    const { data, error } = await supabase
      .from("knock_pins")
      .select("id, lat, lon, current_state")
      .gte("lat", bb.minLat).lte("lat", bb.maxLat)
      .gte("lon", bb.minLon).lte("lon", bb.maxLon)
      .order("id")
      .range(from, from + 999);
    if (error) throw new Error(error.message);
    const page = (data ?? []) as Array<{ id: string; lat: number; lon: number; current_state: string }>;
    for (const p of page) {
      const lat = Number(p.lat), lon = Number(p.lon);
      if (Number.isFinite(lat) && Number.isFinite(lon) && pointInRings(lon, lat, rings)) out.push({ ...p, lat, lon });
    }
    if (page.length < 1000) break;
  }
  return out;
}

export default function AreaMapStormDrawer({ geometry }: { geometry: any }) {
  const mapDiv = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const [storms, setStorms] = useState<AreaStormSig[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // Geometry-derived center + radius (covers the whole boundary + a margin).
  const geo = useMemo(() => {
    const rings = ringsFromGeometry(geometry);
    if (rings.length === 0 || (rings[0]?.length ?? 0) < 3) return null;
    const bb = ringsBBox(rings);
    const centerLat = (bb.minLat + bb.maxLat) / 2;
    const centerLon = (bb.minLon + bb.maxLon) / 2;
    // Radius = farthest corner from center + ~500m buffer, floored at 400m so
    // a tiny lasso still catches a nearby storm cell.
    const corners: Array<[number, number]> = [
      [bb.minLat, bb.minLon], [bb.minLat, bb.maxLon],
      [bb.maxLat, bb.minLon], [bb.maxLat, bb.maxLon],
    ];
    let far = 0;
    for (const c of corners) {
      const d = haversineMetersAM(centerLat, centerLon, c[0], c[1]);
      if (d > far) far = d;
    }
    const radiusM = Math.max(400, far + 500);
    return { rings, bb, centerLat, centerLon, radiusM };
  }, [geometry]);

  // ── Init map once + draw the area boundary, fit to it ────────────────────
  useEffect(() => {
    if (!mapDiv.current || mapRef.current || !geo) return;
    if (typeof mapboxgl === "undefined") { setErr("Map library not loaded"); return; }
    mapboxgl.accessToken = AREA_MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: mapDiv.current,
      style: "mapbox://styles/mapbox/satellite-streets-v12",
      center: [geo.centerLon, geo.centerLat],
      zoom: 14,
      attributionControl: false,
      cooperativeGestures: true,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new mapboxgl.AttributionControl({ compact: true }), "bottom-right");
    mapRef.current = map;

    const fc = {
      type: "FeatureCollection",
      features: [{ type: "Feature", properties: {}, geometry }],
    };
    const drawArea = () => {
      if (!map.getSource("area-boundary")) {
        map.addSource("area-boundary", { type: "geojson", data: fc });
        map.addLayer({
          id: "area-boundary-fill", type: "fill", source: "area-boundary",
          paint: { "fill-color": "#f46530", "fill-opacity": 0.16 },
        });
        map.addLayer({
          id: "area-boundary-line", type: "line", source: "area-boundary",
          paint: { "line-color": "#f46530", "line-width": 3, "line-opacity": 1 },
        });
      }
      // Fit to the boundary
      const all: [number, number][] = geo.rings.flat() as [number, number][];
      const bounds = all.reduce(
        (b: any, c: [number, number]) => b.extend(c),
        new mapboxgl.LngLatBounds(all[0], all[0]),
      );
      map.fitBounds(bounds, { padding: 28, duration: 0, maxZoom: 16.5 });
      setTimeout(() => { try { map.resize(); } catch { /* ignore */ } }, 60);
    };
    if (map.isStyleLoaded()) drawArea();
    else map.once("style.load", drawArea);

    return () => { try { map.remove(); } catch { /* ignore */ } mapRef.current = null; };
  }, [geo, geometry]);

  // ── Fetch the area's knock pins + radar hail; render layers + storm list ──
  useEffect(() => {
    if (!geo) return;
    let cancelled = false;
    const since = new Date(Date.now() - AREA_STORM_DAYS * 86_400_000).toISOString();
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const [sigRes, livePins] = await Promise.all([
          supabase.rpc("canvas_storm_signatures_within_radius", {
            p_lat: geo.centerLat, p_lon: geo.centerLon,
            p_radius_meters: geo.radiusM, p_since: since,
          }),
          fetchPinsInside(geo.rings, geo.bb),
        ]);
        const pinRes = { data: livePins };
        if (cancelled) return;
        if (sigRes.error) { setErr(sigRes.error.message); setLoading(false); return; }

        const sigs: AreaStormSig[] = (sigRes.data ?? []).map((s: any) => ({
          id: s.id,
          occurred_at: s.occurred_at,
          lat: Number(s.lat),
          lon: Number(s.lon),
          max_size_in: Number(s.max_size_in ?? 0),
          sev_prob: s.sev_prob == null ? null : Number(s.sev_prob),
          prob: s.prob == null ? null : Number(s.prob),
        }));
        // Biggest hail first, then most recent.
        sigs.sort((a, b) =>
          b.max_size_in - a.max_size_in ||
          b.occurred_at.localeCompare(a.occurred_at)
        );
        setStorms(sigs);
        setLoading(false);

        const map = mapRef.current;
        if (!map) return;
        const renderLayers = () => {
          // Knock pins — small dots colored by lifecycle state
          const pins = (pinRes.data ?? []) as Array<{ id: string; lat: number; lon: number; current_state: string }>;
          const pinGeo = {
            type: "FeatureCollection",
            features: pins
              .filter((p) => p.lat != null && p.lon != null)
              .map((p) => ({
                type: "Feature",
                properties: { state: p.current_state },
                geometry: { type: "Point", coordinates: [Number(p.lon), Number(p.lat)] },
              })),
          };
          if (!map.getSource("area-pins")) {
            map.addSource("area-pins", { type: "geojson", data: pinGeo });
            map.addLayer({
              id: "area-pins-pts", type: "circle", source: "area-pins",
              paint: {
                "circle-radius": 4,
                "circle-color": [
                  "match", ["get", "state"],
                  "open", "#f97316",
                  "knocked", "#eab308",
                  "contacted", "#a855f7",
                  "rent", "#a855f7",
                  "appt_scheduled", "#06b6d4",
                  "claim_called", "#3b82f6",
                  "built", "#22c55e",
                  "legacy_client", "#14b8a6",
                  /* default (do_not_knock / wrong_address / not_home_3x) */ "#94a3b8",
                ],
                "circle-opacity": 0.85,
                "circle-stroke-color": "#ffffff",
                "circle-stroke-width": 1,
              },
            });
          } else { map.getSource("area-pins").setData(pinGeo); }

          // Radar hail signatures — blue circles sized by hail diameter
          const sigGeo = {
            type: "FeatureCollection",
            features: sigs.map((s) => ({
              type: "Feature",
              properties: { size: s.max_size_in, date: s.occurred_at?.slice(0, 10) },
              geometry: { type: "Point", coordinates: [s.lon, s.lat] },
            })),
          };
          if (!map.getSource("area-sigs")) {
            map.addSource("area-sigs", { type: "geojson", data: sigGeo });
            map.addLayer({
              id: "area-sigs-pts", type: "circle", source: "area-sigs",
              paint: {
                "circle-radius": ["interpolate", ["linear"], ["get", "size"], 0.5, 4, 1.0, 6, 1.5, 9, 2.0, 12, 3.0, 16],
                "circle-color": "#2563eb",
                "circle-opacity": 0.5,
                "circle-stroke-color": "#1e40af",
                "circle-stroke-width": 1,
              },
            });
            map.on("click", "area-sigs-pts", (e: any) => {
              const p = e.features?.[0]?.properties ?? {};
              const c = e.features?.[0]?.geometry?.coordinates;
              if (!c) return;
              new mapboxgl.Popup()
                .setLngLat(c)
                .setHTML(`<div style="font-family:system-ui;font-size:12px"><strong>${Number(p.size).toFixed(2)}″ hail</strong><br/>${p.date}</div>`)
                .addTo(map);
            });
            map.on("mouseenter", "area-sigs-pts", () => { map.getCanvas().style.cursor = "pointer"; });
            map.on("mouseleave", "area-sigs-pts", () => { map.getCanvas().style.cursor = ""; });
          } else { map.getSource("area-sigs").setData(sigGeo); }
        };
        if (map.isStyleLoaded()) renderLayers();
        else map.once("style.load", renderLayers);
      } catch (e: any) {
        if (!cancelled) { setErr(e?.message ?? String(e)); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [geo]);

  if (!geo) {
    return (
      <div className="rounded-md border border-slate-200 bg-white p-3 text-xs text-slate-500">
        No boundary on file for this area — can't render a map.
      </div>
    );
  }

  const largest = storms && storms.length ? storms[0].max_size_in : 0;
  const topStorms = storms ? storms.slice(0, 15) : [];

  return (
    <div className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-2 md:flex-row">
      {/* Map */}
      <div className="md:w-1/2">
        <div ref={mapDiv} className="h-64 w-full overflow-hidden rounded-md border border-slate-200" />
        {err ? <div className="mt-1 rounded bg-red-50 px-2 py-1 text-[11px] text-red-700">⚠ {err}</div> : null}
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-slate-500">
          <span><span className="mr-1 inline-block h-2 w-3 rounded-sm border border-orange-500 bg-orange-300/40 align-middle" />Assigned area</span>
          <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-orange-500 align-middle" />Open</span>
          <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-purple-500 align-middle" />Contacted</span>
          <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-green-500 align-middle" />Built</span>
          <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-blue-600/70 align-middle" />Hail (sized)</span>
        </div>
      </div>

      {/* Storm / hail list */}
      <div className="md:w-1/2">
        <div className="mb-1 flex items-baseline justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
            ⛈ Hail in this area · last 12 mo
          </div>
          {storms ? (
            <div className="text-[10px] text-slate-500">
              {storms.length} {storms.length === 1 ? "signature" : "signatures"}
              {largest > 0 ? <> · largest <span className="font-semibold text-slate-700">{largest.toFixed(2)}″</span></> : null}
            </div>
          ) : null}
        </div>

        {loading ? (
          <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-500">Loading hail history…</div>
        ) : topStorms.length === 0 ? (
          <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-500">
            No NEXRAD radar hail signatures in this area over the last 12 months.
          </div>
        ) : (
          <div className="max-h-56 overflow-y-auto rounded-md border border-slate-100">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-2 py-1 text-left">Hail</th>
                  <th className="px-2 py-1 text-left">Date</th>
                  <th className="px-2 py-1 text-right">POH / POSH</th>
                </tr>
              </thead>
              <tbody>
                {topStorms.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100">
                    <td className="px-2 py-1">
                      <span className={`rounded px-1.5 py-0.5 text-[11px] font-bold tabular-nums ${hailBadgeClasses(s.max_size_in)}`}>
                        {s.max_size_in.toFixed(2)}″
                      </span>
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap text-slate-700">
                      {s.occurred_at ? new Date(s.occurred_at).toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "numeric" }) : "—"}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-slate-500">
                      {s.prob ?? "—"} / {s.sev_prob ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {storms && storms.length > topStorms.length ? (
              <div className="bg-slate-50 px-2 py-1 text-center text-[10px] text-slate-400">
                + {storms.length - topStorms.length} more (showing 15 largest)
              </div>
            ) : null}
          </div>
        )}
        <div className="mt-1 text-[10px] text-slate-400">
          NEXRAD MEHS within ~{(geo.radiusM / 1609.344).toFixed(1)} mi of the area center · POH = hail probability, POSH = severe-hail probability.
        </div>
      </div>
    </div>
  );
}
